import { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { useNavigate } from "react-router";
import { Copy } from "lucide-react";

const CLONE_AGREEMENT = gql`
  mutation CloneAgreement($id: String!) {
    cloneAgreement(id: $id) {
      id
      name
    }
  }
`;

interface CloneActionProps {
  agreementId: string;
  agreementName: string;
  hasUnsavedChanges: boolean;
}

export function CloneAction({
  agreementId,
  agreementName,
  hasUnsavedChanges,
}: CloneActionProps) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [clone, { loading }] = useMutation<{
    cloneAgreement: { id: string; name: string };
  }>(CLONE_AGREEMENT);

  async function handleConfirm() {
    try {
      const res = await clone({ variables: { id: agreementId } });
      const newId = res.data?.cloneAgreement.id;
      if (newId) {
        navigate(`/settings/agreements/${newId}`);
      }
    } catch (e) {
      console.error("Clone failed", e);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={loading}
        className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Copy size={12} strokeWidth={1.8} />
        Clone
      </button>

      {confirming && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-6">
          <div className="bg-paper-hi rounded-lg shadow-lift w-full max-w-md p-5">
            <h3 className="font-display text-[20px] font-medium tracking-tight">
              Clone "{agreementName}"?
            </h3>
            <p className="text-[12.5px] text-ink-3 mt-2 leading-relaxed">
              Creates a copy with all content and design overrides. The
              clone starts as inactive and gets its own preview.
              {hasUnsavedChanges && (
                <span className="block mt-2 text-spark">
                  You have unsaved changes — the clone will use the last
                  saved version, not the draft.
                </span>
              )}
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={loading}
                className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:opacity-50"
              >
                {loading ? "Cloning…" : "Clone"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
